import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingType, MeetingStatus, EmployeeDisplayTinyInfo, MeetingRequestStatus, Meeting } from '../../../types'
import { meetingTagUiProps } from '../../../components/custom_components/MeetingTag/meetingTag.uiprops'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxNumberElement } from '../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { PerInfoBoxTagElement } from '../../../components/custom_components/InfoBox/InfoBoxTagElement'
import { PerInfoBoxDivider } from '../../../components/custom_components/InfoBox/InfoBoxDivider'
import { NewMeetingInviteButton } from '../invite/NewMeetingInviteButton'
import { FutureMeetingElement } from './FutureMeetingElement'
import { Select } from 'antd'
import PerFlexRow from '../../../components/custom_components/FlexRow'

type MeetingViewInfoBoxProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  isManagerMeetings?: {
    employeeList: EmployeeDisplayTinyInfo[]
  }
  isRequestedMeetings?: boolean
  meetingRequestsFilter?: MeetingRequestStatus
  onMeetingRequestsFilterChange?: (value: MeetingRequestStatus) => void
}

export const MeetingViewInfoBox: FC<MeetingViewInfoBoxProps> = ({
  data,
  isManagerMeetings,
  isRequestedMeetings,
  meetingRequestsFilter,
  onMeetingRequestsFilterChange,
}) => {
  const { t } = useTranslation()
  const { tagColor, tagName } = meetingTagUiProps(t)

  const appraisal = MeetingType.Appraisal
  const appraisalUpdate = MeetingType.AppraisalUpdate
  const projectFeedback = MeetingType.OtherProjectFeedback
  const probation = MeetingType.OtherProbationTalk
  const general = MeetingType.OtherGeneric

  const totalOfMeetingType = (meetingType: MeetingType) => {
    return data.filter((meeting: Meeting) => meeting.type === meetingType).length
  }
  const totalOfAppraisal = totalOfMeetingType(appraisal)
  const totalOfAppraisalUpdate = totalOfMeetingType(appraisalUpdate)
  const totalOfProjectFeedback = totalOfMeetingType(projectFeedback)
  const totalOfProbation = totalOfMeetingType(probation)
  const totalOfGeneral = totalOfMeetingType(general)

  const futureMeetingData = data.filter((meeting) => meeting.status !== MeetingStatus.PendingConfirmation)
  const { Option } = Select
  const headingString = meetingRequestsFilter ? meetingRequestsFilter : 'Requested'

  const renderOptions = () => {
    return Object.keys(MeetingRequestStatus).map((key) => {
      const value = MeetingRequestStatus[key as keyof typeof MeetingRequestStatus]
      return (
        <Option key={value} value={value}>
          {value}
        </Option>
      )
    })
  }

  return (
    <PerInfoBox>
      {meetingRequestsFilter && onMeetingRequestsFilterChange ? (
        <PerFlexRow direction="column" className="fullHeight per-flexRow--fixed">
          <div className="per-appraisal-info-box--select-text-color">
            <Select
              className="per-text--tile-heading"
              defaultValue={MeetingRequestStatus.Requested}
              onChange={(value: MeetingRequestStatus) => onMeetingRequestsFilterChange(value)}
            >
              {renderOptions()}
            </Select>
          </div>

          <PerInfoBoxNumberElement number={data.length} />
        </PerFlexRow>
      ) : (
        <InfoBoxTile heading={isRequestedMeetings ? headingString : 'Meetings'} contentFlex="fixed">
          <PerInfoBoxNumberElement number={data.length} />
        </InfoBoxTile>
      )}

      <PerInfoBoxDivider />

      <InfoBoxTile heading="Meeting Types">
        <PerInfoBoxTagElement name={tagName(appraisal)} number={totalOfAppraisal} color={tagColor(appraisal)} />
        <PerInfoBoxTagElement name={tagName(appraisalUpdate)} number={totalOfAppraisalUpdate} color={tagColor(appraisalUpdate)} />
        <PerInfoBoxTagElement name={tagName(projectFeedback)} number={totalOfProjectFeedback} color={tagColor(projectFeedback)} />
        <PerInfoBoxTagElement name={tagName(general)} number={totalOfGeneral} color={tagColor(general)} />
        <PerInfoBoxTagElement name={tagName(probation)} number={totalOfProbation} color={tagColor(probation)} />
      </InfoBoxTile>

      {isManagerMeetings && (
        <>
          <PerInfoBoxDivider />

          {futureMeetingData && !!futureMeetingData.length && (
            <>
              <InfoBoxTile heading="Next Meeting" contentFlex="shrink">
                <FutureMeetingElement meetingData={futureMeetingData} />
              </InfoBoxTile>

              <PerInfoBoxDivider />
            </>
          )}

          <InfoBoxTile heading="Invite" contentFlex="fixed" headingAlign="center" contentAlign="center">
            <NewMeetingInviteButton employeeList={isManagerMeetings.employeeList} btnSize="large" />
          </InfoBoxTile>
        </>
      )}
    </PerInfoBox>
  )
}
